import React, { useState } from "react";

import {
    Button,
    ButtonGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    FormControl,
    FormLabel,
    FormControlLabel,
    Checkbox,
    Grid,
    Box,
} from "@mui/material";
import { MenuBook } from "@mui/icons-material";

import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import { patchProjectAdvice } from "../../ProjectActions";
import { global, importanceLevels } from "../../../../context/global";
import DesignPrincipleDialog from "./DesignPrincipleDialog";
import AttachmentTable from "../../../Attachments/AttachmentTable";
import StepperButtons from "../../StepperButtons";
import InformationDialogue from "../../../CustomComponents/InformationDialogue";
import { advice } from "../../../../context/designPhase";
import { Pluralize } from "../../../../functions/Formatters";

export default function ProjectAdvice({ payload }) {
    const dispatch = useDispatch();
    const flatThemes = useSelector((state) => state.themes.flatThemes);
    const { project, loop, phase } = payload;
    const themes = loop?.loop_themes || [];

    const [minimumImportanceFilter, setMinimumImportanceFilter] = useState(new Set([0,1,2,3]));
    const updateImportanceFilter = (level, include) => {
        const newMinimumImportanceFilter = new Set(minimumImportanceFilter);
        if (include) {
            newMinimumImportanceFilter.add(level);
        } else {
            newMinimumImportanceFilter.delete(level);
        }
        setMinimumImportanceFilter(newMinimumImportanceFilter);
    }
    
    const filteredThemes = themes.filter((theme) => minimumImportanceFilter.has(theme.importance)).sort((a, b) => flatThemes[a.theme].supertheme - flatThemes[b.theme].supertheme);

    // Group themes by supertheme
    const groupedThemes = React.useMemo(() => {
        const groups = {};
        
        // Group themes by supertheme
        filteredThemes.forEach(projectTheme => {
            const themeInfo = flatThemes[projectTheme.theme];
            if (themeInfo) {
                const themeKey = `${themeInfo.supertheme}-${themeInfo.name}`;
                if (!groups[themeKey]) {
                    groups[themeKey] = {
                        theme: themeInfo,
                        projectThemes: []
                    };
                }
                groups[themeKey].projectThemes.push(projectTheme);
            }
        });
        
        // Sort groups by supertheme, then by theme name
        const sortedGroups = Object.values(groups).sort((a, b) => {
            const superthemeComparison = a.theme.supertheme - b.theme.supertheme;
            if (superthemeComparison !== 0) {
                return superthemeComparison;
            }
            return a.theme.name.localeCompare(b.theme.name);
        });
        
        // Sort project themes within each group by importance
        sortedGroups.forEach(group => {
            group.projectThemes.sort((a, b) => a.importance - b.importance);
        });
        
        return sortedGroups;
    }, [filteredThemes, flatThemes]);

    const [openPrincipleDialog, setOpenPrincipleDialog] = useState(false);
    const [designPrincipleFocus, setDesignPrincipleFocus] = useState(false);

    const showFocusedDesignPrinciples = (themeID) => {
        setDesignPrincipleFocus(themeID);
        setOpenPrincipleDialog(true);
    };

    // Add a check to toggle editable. Inputs can can only be changed in the last loop of the last phase.
    const lastPhase = payload.project.phases.slice(-1)[0];
    const lastLoop = lastPhase.loops.slice(-1)[0];
    payload.editable = payload?.loop?.id === lastLoop?.id ? payload.editable : false;


    return (
        <>
            <InformationDialogue text={advice} />
            <DesignPrincipleDialog open={openPrincipleDialog} onClose={() => setOpenPrincipleDialog(false)} focus={designPrincipleFocus} setFocus={setDesignPrincipleFocus} />
            <Stack spacing={1}>
                <Typography>
                    {`Hieronder kunt per ${global.theme} de de ontwerpadviezen invullen voor de huidige ontwerpronde. 
                    Met de knop 'Ontwerpprincipes' kunt u zien welke ontwerpprincipes van toepassing zijn op het ${global.theme}.`}
                </Typography>

                {/* Design principle button */}
                <ButtonGroup>
                    <Button variant="contained" onClick={() => showFocusedDesignPrinciples(false)} startIcon={<MenuBook />}>
                        Alle ontwerpprincipes
                    </Button>
                </ButtonGroup>

                {/* Importance filter */}
                <FormControl>
                    <FormLabel>Selecteer hier welke niveaus u wilt zien</FormLabel>
                    <Grid container spacing={1} maxWidth={'450px'}>
                    {importanceLevels.map((level) => (
                        <Grid item xs={12} sm={6} key={level.value}>
                        <FormControlLabel 
                        key={level.value}
                        label={level.label} 
                        control={
                            <Checkbox 
                            checked={minimumImportanceFilter.has(level.value)} 
                            onChange={(e) => updateImportanceFilter(level.value, e.target.checked)} />
                        }
                        />
                        </Grid>
                    ))}
                    </Grid>
                </FormControl>

                {groupedThemes.length > 0 ? (
                    <Box>
                        {groupedThemes.map((group) => (
                            <Box key={`${group.theme.supertheme}-${group.theme.name}`} sx={{ mb: 3 }}>
                                {/* Theme Header */}
                                <Box sx={{ mb: 1 }}>
                                    <Tooltip 
                                        title={group.theme.description || "Geen beschrijving beschikbaar"} 
                                        placement="top"
                                        arrow
                                    >
                                        <Typography 
                                            variant="h6" 
                                            component="h3"
                                            sx={{ 
                                                fontWeight: 'bold',
                                                color: 'text.primary',
                                                cursor: 'help',
                                                borderBottom: 2,
                                                borderColor: 'primary.main',
                                                pb: 0.5,
                                                display: 'inline-block'
                                            }}
                                        >
                                            {group.theme.name}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                                
                                {/* Project themes under this supertheme */}
                                <Box sx={{ ml: 2 }}>
                                    {group.projectThemes.map((projecttheme, projectThemeIndex) => (
                                        <Box key={projecttheme.id} sx={{ mb: 2 }}>
                                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                                                Niveau: {importanceLevels[projecttheme.importance].label}
                                            </Typography>
                                            {projecttheme?.description && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Beschrijving: {projecttheme.description}
                                                </Typography>
                                            )}
                                            <Button 
                                                startIcon={<MenuBook />} 
                                                variant="contained" 
                                                color="secondary" 
                                                onClick={() => showFocusedDesignPrinciples(projecttheme.theme)} 
                                                sx={{ mb: 2 }}
                                                size="small"
                                            >
                                                Ontwerpprincipes
                                            </Button>
                                            
                                            <Formik
                                                enableReinitialize
                                                initialValues={{
                                                    advice:
                                                        (() => {
                                                            const loopTheme = themes.find((lt) => lt.theme === projecttheme.theme);
                                                            const loopThemeId = loopTheme?.id;
                                                            return (loopThemeId && loop?.advice_by_themes?.[loopThemeId]) || { id: null, text: "", attachments: [] };
                                                        })(),
                                                }}
                                                onSubmit={(values, { setSubmitting }) => {
                                                    const adviceObj = values.advice;
                                                    if (payload.editable && adviceObj?.id) {
                                                        const loopTheme = themes.find((lt) => lt.theme === projecttheme.theme);
                                                        const loopThemeId = loopTheme?.id;
                                                        patchProjectAdvice(
                                                            adviceObj,
                                                            payload.project,
                                                            { phaseID: phase.id, loopID: loop?.id, loopThemeId },
                                                            dispatch
                                                        );
                                                    }
                                                    setSubmitting(false);
                                                }}
                                            >
                                                {({ values, handleSubmit }) => (
                                                    <Form>
                                                        <TableContainer>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Ontwerpadvies</TableCell>
                                                                        <TableCell>Bijlagen</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    <TableRow>
                                                                        <TableCell sx={{ minWidth: 400 }}>
                                                                            <Field
                                                                                component={TextField}
                                                                                name={`advice.text`}
                                                                                inputProps={{ id: `advice.text` }}
                                                                                multiline
                                                                                minRows={2}
                                                                                maxRows={5}
                                                                                fullWidth
                                                                                disabled={!payload.editable}
                                                                                onBlur={() => handleSubmit()}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell sx={{ width: 0 }}>
                                                                            <AttachmentTable
                                                                                parentObj={values.advice}
                                                                                parentType="advice"
                                                                                parentIds={{
                                                                                    project: project?.id,
                                                                                    phase: phase?.id,
                                                                                    loop: loop?.id,
                                                                                    themeID: loop?.loop_themes?.find((lt) => lt.theme === projecttheme.theme)?.id,
                                                                                }}
                                                                                defaultExpanded={false}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </Form>
                                                )}
                                            </Formik>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body1">{themes.length === 0 ? `Er zijn nog geen ${Pluralize(global.theme)} toegevoegd aan dit project.` : `Er zijn geen ${Pluralize(global.theme)} van het geselecteerde niveau.`}</Typography>
                )}
                <StepperButtons payload={payload} updateStatus={false} />
            </Stack>
        </>
    );
}
